// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { StatusChip } from '@/components/status-chip';
import { ArrowForward, ExpandMore } from '@/constants/hardfin-standard-icons';
import { PurchaseOrderItem } from '@/purchase-order/api/types';
import {
  LINE_ITEM_STATUS_TOOLTIPS,
  PHASE_CONFIG,
  getModifierVariant,
  parseStatus,
} from '@/purchase-order/constants/status';
import { getStatusIcon } from '@/purchase-order/constants/status-icons';
import { Shipment } from '@/shipment/api/types';
import { ShipmentStatusChip } from '@/shipment/components/shipment-status-chip';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

interface LineItemsTableProps {
  items: PurchaseOrderItem[];
  shipments: Shipment[];
}

interface ReceivedColumnProps {
  open: number;
  shipped: number;
  delivered: number;
  received: number;
}

/**
 * Component that displays the unified received status column showing
 * open, shipped, delivered, and received quantities in an outlined box.
 */
function ReceivedColumn(props: ReceivedColumnProps) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 1.5,
        minWidth: 280,
      }}
    >
      <Stack direction="row" spacing={2} justifyContent="center">
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Open
          </Typography>
          <Typography variant="body1">{formatNumber(props.open)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Shipped
          </Typography>
          <Typography variant="body1">{formatNumber(props.shipped)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Delivered
          </Typography>
          <Typography variant="body1">
            {formatNumber(props.delivered)}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Received
          </Typography>
          <Typography variant="body1">
            {formatNumber(props.received)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

interface RelatedShipmentsTableProps {
  shipments: Shipment[];
  itemId: string;
}

/**
 * Component that displays related shipments for a line item.
 */
function RelatedShipmentsTable(props: RelatedShipmentsTableProps) {
  // Filter and flatten shipment items to show only those matching this line item's item
  const filteredShipmentItems = useMemo(() => {
    const items: {
      shipment: Shipment;
      quantity: number;
    }[] = [];

    props.shipments.forEach((shipment) => {
      shipment.items.forEach((shipmentItem) => {
        if (shipmentItem.item.id === props.itemId) {
          items.push({
            shipment,
            quantity: shipmentItem.quantity ?? 0,
          });
        }
      });
    });

    // Sort by ship date descending (most recent first), with shipment ID as secondary sort for stability
    items.sort((a, b) => {
      const aDate = a.shipment.shippedAt?.toMillis() ?? 0;
      const bDate = b.shipment.shippedAt?.toMillis() ?? 0;

      // Sort by ship date descending
      if (bDate !== aDate) {
        return bDate - aDate;
      }

      // Secondary sort by shipment ID for stable sort
      return a.shipment.id.localeCompare(b.shipment.id);
    });

    return items;
  }, [props.shipments, props.itemId]);

  if (filteredShipmentItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No related shipments
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Related shipments
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Shipment ID</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Ship date</TableCell>
              <TableCell>Delivered date</TableCell>
              <TableCell>Received date</TableCell>
              <TableCell>Tracking</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredShipmentItems.map((item, index) => {
              const { shipment, quantity } = item;

              return (
                <TableRow key={`${shipment.id}-${index}`}>
                  <TableCell>
                    <ShipmentStatusChip status={shipment.status} />
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/shipments/$shipmentKey"
                      params={{ shipmentKey: shipment.key }}
                      search={{ type: 'inbound' }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {shipment.shipmentNumber}
                      <ArrowForward fontSize="small" />
                    </Link>
                  </TableCell>
                  <TableCell align="right">{formatNumber(quantity)}</TableCell>
                  <TableCell>
                    {shipment.shippedAt
                      ? shipment.shippedAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {shipment.deliveredAt
                      ? shipment.deliveredAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {shipment.receivedAt
                      ? shipment.receivedAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <ShippingCarrierTrackingLabel
                      carrier={shipment.trackingNumberDetails?.carrier ?? null}
                      trackingIdentifier={
                        shipment.trackingNumberDetails?.trackingNumber ?? null
                      }
                      linkNumberDirectly={true}
                      variant="body2"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/**
 * Component that displays the line items of a purchase order in a card-based layout.
 * Shows product information and quantity tracking with inline labels.
 */
export function PurchaseOrderLineItemsTable(props: LineItemsTableProps) {
  const { items, shipments } = props;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  if (items.length === 0) {
    return (
      <Box padding={3}>
        <Typography variant="body2" color="text.secondary">
          No line items
        </Typography>
      </Box>
    );
  }

  return items.map((item, index) => {
    const requested = item.requestedQuantity;

    const open = item.openQuantity;
    const shipped = item.shippedQuantity;
    const delivered = item.deliveredQuantity;
    const received = item.receivedQuantity;
    const need = Math.max(
      0,
      requested - (open + shipped + delivered + received)
    );

    // Determine item display name and SKU
    const productName = item.item.name;
    const productSku = item.item.sku;

    const isExpanded = expandedItems.has(item.id);

    return (
      <Box
        key={item.id}
        sx={{
          ...(index < items.length - 1 && {
            borderBottom: '1px solid',
            borderColor: 'divider',
          }),
          paddingY: 1,
          paddingX: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Left side - Status and Product */}
          <Stack direction="row" spacing={2} alignItems="center" flex={1}>
            {(() => {
              const { phase, modifier } = parseStatus(item.status);
              const config = PHASE_CONFIG[phase];
              return (
                <Tooltip title={LINE_ITEM_STATUS_TOOLTIPS[item.status]} arrow>
                  <StatusChip
                    icon={getStatusIcon(phase, modifier)}
                    label={config.label}
                    color={config.chipConfig.color}
                    variant={getModifierVariant(phase, modifier)}
                  />
                </Tooltip>
              );
            })()}
            <Box>
              <Typography variant="body2">
                {productSku}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  ({productName})
                </Typography>
              </Typography>
            </Box>
          </Stack>

          {/* Right side - Quantities */}
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Requested column */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary">
                Requested
              </Typography>
              <Typography variant="body1">{formatNumber(requested)}</Typography>
            </Box>

            {/* Received column - outlined with combined metrics */}
            <ReceivedColumn
              open={open}
              shipped={shipped}
              delivered={delivered}
              received={received}
            />

            {/* Need column */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary">
                Need
              </Typography>
              <Typography variant="body1">{formatNumber(need)}</Typography>
            </Box>

            {/* Expand button */}
            <IconButton
              size="small"
              onClick={() => toggleExpanded(item.id)}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Stack>
        </Stack>

        {/* Expanded section with related shipments */}
        <Collapse in={isExpanded}>
          <Box paddingY={1} paddingX={2}>
            <RelatedShipmentsTable
              shipments={shipments}
              itemId={item.item.id}
            />
          </Box>
        </Collapse>
      </Box>
    );
  });
}
